import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, SaveIcon } from '@app/components/icons';
import OrgModuleLayout from '@app/pages/org/OrgModuleLayout';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { resolveErrorMessage } from '@app/utils/errors';
import { formatNumber } from '@app/utils/format';
import { streamSse } from '@app/utils/sse';
import {
  compileRunbookFromText,
  getRunbookCompilation,
  getRunbookCompilationEventsUrl,
  type CompileTemplateResult,
  type RunbookCompileAcceptedResponse,
  type RunbookCompileEvent,
  type RunbookCompileStatusResponse
} from './api';

const formatToken = (value?: string | null, fallback = 'Draft'): string => {
  const token = (value ?? '').trim();
  if (!token) {
    return fallback;
  }
  return token
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');
};

const statusBadgeClass = (value?: string | null): string => {
  switch ((value ?? '').trim().toLowerCase()) {
    case 'published':
    case 'ready':
      return 'ui-badge ui-badge--success';
    case 'needs_review':
    case 'blocked':
      return 'ui-badge ui-badge--danger';
    case 'draft':
      return 'ui-badge ui-badge--warning';
    default:
      return 'ui-badge ui-badge--ghost';
  }
};

const stageLabel = (value?: string | null): string => formatToken(value, 'Starting');

const validationIssueTitle = (issue: { stage?: string; code?: string; severity?: string }): string => {
  if (issue.stage || issue.code) {
    return [formatToken(issue.stage, ''), issue.code].filter(Boolean).join(' · ');
  }
  return formatToken(issue.severity, 'Compiler issue');
};

const compilationStatusText = (value?: string | null): string => {
  switch ((value ?? '').trim().toLowerCase()) {
    case 'queued':
      return 'Queued';
    case 'running':
      return 'Compiling';
    case 'succeeded':
      return 'Compiled';
    case 'failed':
      return 'Failed';
    default:
      return formatToken(value, 'Idle');
  }
};

const COMPILATION_EVENT_LIMIT = 8;

const RunbookNewPage = () => {
  const navigate = useNavigate();
  const apiConfig = useApiConfiguration();
  const authFetch = useAuthFetch();
  const orgId = useActiveOrgId();

  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CompileTemplateResult | null>(null);
  const [compilation, setCompilation] = useState<RunbookCompileStatusResponse | null>(null);
  const [acceptedCompile, setAcceptedCompile] = useState<RunbookCompileAcceptedResponse | null>(null);
  const [compileEvents, setCompileEvents] = useState<RunbookCompileEvent[]>([]);
  const [streamState, setStreamState] = useState<'idle' | 'connecting' | 'open' | 'closed' | 'error'>('idle');
  const streamControllerRef = useRef<AbortController | null>(null);

  const canSubmit = useMemo(() => {
    if (!orgId || !title.trim()) {
      return false;
    }
    return instructions.trim().length > 0;
  }, [instructions, orgId, title]);

  const draftStats = useMemo(() => {
    const words = instructions.trim() ? instructions.trim().split(/\s+/).length : 0;
    const lines = instructions.trim() ? instructions.trim().split(/\n+/).filter(Boolean).length : 0;
    return { words, lines };
  }, [instructions]);

  const resultStats = useMemo(() => {
    const validationErrors = result?.diagnostics?.validation_errors?.length ?? 0;
    const parameters = result?.template_ir?.parameters?.length ?? 0;
    const requiredParameters = result?.template_ir?.parameters?.filter((item) => item.required).length ?? 0;
    const steps = result?.template_ir?.steps?.length ?? 0;
    return { validationErrors, parameters, requiredParameters, steps };
  }, [result]);
  const validationIssues = result?.diagnostics?.validation_errors ?? [];
  const reviewReason = result?.diagnostics?.review_reason;

  const compilationPercent = Math.max(0, Math.min(100, compilation?.percent ?? 0));
  const latestCompileMessage = compileEvents[0]?.message ?? null;

  useEffect(() => {
    return () => {
      streamControllerRef.current?.abort();
      streamControllerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!orgId || !acceptedCompile?.compilation_id) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    streamControllerRef.current?.abort();
    streamControllerRef.current = controller;
    setStreamState('connecting');

    const syncStatus = async () => {
      try {
        const next = await getRunbookCompilation(authFetch.fetchWithAuthRetry, apiConfig.basePath ?? '', orgId, acceptedCompile.compilation_id);
        if (cancelled) {
          return;
        }
        setCompilation(next);
        if (next.result) {
          setResult(next.result);
        }
        if (next.status === 'failed' && next.error) {
          setError(next.error);
          setSubmitting(false);
        }
        if (next.status === 'succeeded') {
          setSubmitting(false);
        }
      } catch (caught) {
        if (!cancelled && !controller.signal.aborted) {
          console.error('Failed to fetch runbook compilation status', caught);
        }
      }
    };

    void streamSse(
      getRunbookCompilationEventsUrl(apiConfig.basePath ?? '', orgId, acceptedCompile.compilation_id),
      {
        onOpen: () => {
          if (!cancelled) {
            setStreamState('open');
          }
        },
        onEvent: (type, payload) => {
          if (cancelled) {
            return;
          }
          try {
            const event = JSON.parse(payload) as RunbookCompileEvent;
            setCompileEvents((current) => [event, ...current].slice(0, COMPILATION_EVENT_LIMIT));
            setCompilation((current) => ({
              compilation_id: current?.compilation_id ?? acceptedCompile.compilation_id,
              org_id: current?.org_id ?? orgId,
              status: event.status ?? current?.status ?? 'running',
              current_stage: event.stage ?? current?.current_stage,
              percent: event.percent ?? current?.percent ?? 0,
              error: type === 'error' ? event.message ?? current?.error : current?.error,
              attempt_id: event.attemptId ?? current?.attempt_id,
              created_at: current?.created_at,
              updated_at: event.updatedAt ?? current?.updated_at,
              result: event.result ?? current?.result
            }));

            if (event.result) {
              setResult(event.result);
            }
            if (type === 'finished' || event.status === 'succeeded') {
              setSubmitting(false);
              setStreamState('closed');
              controller.abort();
              return;
            }
            if (type === 'error' || event.status === 'failed') {
              setError(event.message ?? 'Runbook compilation failed.');
              setSubmitting(false);
            }
          } catch (caught) {
            console.error('Failed to parse runbook compilation event', caught);
          }
        }
      },
      controller.signal
    )
      .catch((caught) => {
        if (cancelled || controller.signal.aborted) {
          return;
        }
        console.error('Failed to stream runbook compilation events', caught);
        setStreamState('error');
        void syncStatus();
      })
      .finally(() => {
        if (streamControllerRef.current === controller) {
          streamControllerRef.current = null;
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (streamControllerRef.current === controller) {
        streamControllerRef.current = null;
      }
    };
  }, [acceptedCompile, apiConfig.basePath, authFetch.fetchWithAuthRetry, orgId]);

  const handleSubmit = async () => {
    if (!orgId || !canSubmit || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    setResult(null);
    setCompileEvents([]);
    setCompilation(null);
    setAcceptedCompile(null);
    streamControllerRef.current?.abort();
    streamControllerRef.current = null;
    setStreamState('idle');
    try {
      const next = await compileRunbookFromText(authFetch.fetchWithAuthRetry, apiConfig.basePath ?? '', {
        orgId,
        runbookTitle: title.trim(),
        sourceText: instructions.trim()
      });
      setAcceptedCompile(next);
      setCompilation({
        compilation_id: next.compilation_id,
        org_id: orgId,
        status: next.status,
        percent: 0
      });
    } catch (caught) {
      setError(await resolveErrorMessage(caught, 'We could not save this runbook right now.'));
      setSubmitting(false);
    }
  };

  return (
    <OrgModuleLayout
      title="New Runbook"
      description="Draft natural-language instructions and compile them into a saved template."
      actions={
        <div className="runbooks__actions">
          <button type="button" className="ui-button ui-button--ghost" onClick={() => navigate('/app/runbooks/library')}>
            Back to library
          </button>
          <button type="button" className="ui-button ui-button--primary" onClick={() => void handleSubmit()} disabled={!canSubmit || submitting}>
            <SaveIcon size={16} />
            {submitting ? 'Saving…' : 'Save runbook'}
          </button>
        </div>
      }>
      {error ? (
        <div className="org-shell__banner" role="alert">
          {error}
        </div>
      ) : null}

      <div className="runbooks__editor-grid">
        <section className="runbooks__author-panel">
          <div className="runbooks__section-heading">
            <div>
              <span className="runbooks__eyebrow">Source</span>
              <h3>Authored Procedure</h3>
            </div>
            <span className={canSubmit ? 'ui-badge ui-badge--success' : 'ui-badge ui-badge--ghost'}>{canSubmit ? 'Ready to save' : 'Draft'}</span>
          </div>

          <label className="form-field">
            <span className="form-field__label">Runbook title</span>
            <input className="input" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="check_vlan_status" />
            <span className="form-field__hint">Used as the saved template name for chat and agent invocation.</span>
          </label>

          <label className="form-field">
            <span className="form-field__label">Natural-language instructions</span>
            <textarea
              className="textarea runbooks__instructions-textarea"
              rows={16}
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
              placeholder="Check VLAN status on the target switch, list the configured VLANs, and produce a short markdown summary."
            />
            <span className="form-field__hint">The template compiler will extract parameters and steps, but operators will primarily see this authored text.</span>
          </label>

          <div className="runbooks__draft-stats" aria-label="Draft source metrics">
            <article>
              <span className="runbooks__metric-label">Words</span>
              <strong>{formatNumber(draftStats.words)}</strong>
            </article>
            <article>
              <span className="runbooks__metric-label">Lines</span>
              <strong>{formatNumber(draftStats.lines)}</strong>
            </article>
            <article>
              <span className="runbooks__metric-label">Source</span>
              <strong>Text</strong>
            </article>
          </div>
        </section>

        <section className="runbooks__preview-panel">
          <div className="runbooks__section-heading">
            <div>
              <span className="runbooks__eyebrow">Compiler</span>
              <h3>Saved Template Preview</h3>
            </div>
            {result ? <span className={statusBadgeClass(result.template_status || result.status)}>{formatToken(result.template_status || result.status)}</span> : null}
          </div>

          {compilation ? (
            <div className="runbooks__compile-status" aria-live="polite">
              <div className="runbooks__compile-status-top">
                <div>
                  <strong>{compilationStatusText(compilation.status)}</strong>
                  <span>{stageLabel(compilation.current_stage)}</span>
                </div>
                <span className={statusBadgeClass(compilation.status)}>{compilationPercent}%</span>
              </div>
              <div className="runbooks__progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={compilationPercent}>
                <span style={{ width: `${compilationPercent}%` }} />
              </div>
              <p className="runbooks__muted">
                {latestCompileMessage || (streamState === 'connecting' ? 'Opening live compiler stream…' : 'Waiting for compiler updates…')}
              </p>
              {compileEvents.length ? (
                <ul className="runbooks__list runbooks__list--compact">
                  {compileEvents.slice(0, 4).map((event) => (
                    <li key={`${event.seq}-${event.stage ?? event.status ?? 'event'}`}>
                      <strong>{stageLabel(event.stage || event.status)}</strong>
                      <span>{event.message || compilationStatusText(event.status)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {result ? (
            <div className="runbooks__preview">
              {result.normalized_summary ? <p className="runbooks__summary-callout">{result.normalized_summary}</p> : null}
              <div className="runbooks__detail-grid">
                <article>
                  <span className="runbooks__metric-label">Parameters</span>
                  <strong>{formatNumber(resultStats.parameters)}</strong>
                  <span>{formatNumber(resultStats.requiredParameters)} required</span>
                </article>
                <article>
                  <span className="runbooks__metric-label">Steps</span>
                  <strong>{formatNumber(resultStats.steps)}</strong>
                  <span>compiled actions</span>
                </article>
                <article>
                  <span className="runbooks__metric-label">Diagnostics</span>
                  <strong>{formatNumber(resultStats.validationErrors)}</strong>
                  <span>validation issues</span>
                </article>
              </div>

              {result.missing_input_details?.length ? (
                <div className="runbooks__section">
                  <h4>Missing static inputs</h4>
                  <ul className="runbooks__list">
                    {result.missing_input_details.map((item, index) => (
                      <li key={`${item.name ?? 'input'}-${index}`}>
                        <strong>{item.name || 'Input'}</strong>
                        <span>{item.message || item.description || 'Required before execution planning.'}</span>
                        {item.choices?.length ? <small>{item.choices.join(', ')}</small> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {reviewReason || validationIssues.length ? (
                <div className="runbooks__section">
                  <h4>{reviewReason ? 'Needs review reason' : 'Validation diagnostics'}</h4>
                  {reviewReason ? (
                    <ul className="runbooks__diagnostic-list">
                      <li>
                        <div>
                          <strong>{[formatToken(reviewReason.category, ''), reviewReason.code].filter(Boolean).join(' · ') || 'Compiler review required'}</strong>
                          <p>{reviewReason.message || reviewReason.primary_issue?.message || 'The compiler returned a needs-review result.'}</p>
                          <small>
                            {[reviewReason.stage ? `Stage ${formatToken(reviewReason.stage)}` : null, typeof reviewReason.issue_count === 'number' ? `${formatNumber(reviewReason.issue_count)} issue${reviewReason.issue_count === 1 ? '' : 's'}` : null, reviewReason.repairable === false ? 'Not repairable automatically' : null]
                              .filter(Boolean)
                              .join(' · ')}
                          </small>
                        </div>
                      </li>
                    </ul>
                  ) : null}
                  {validationIssues.length ? (
                    <>
                      {reviewReason ? <h4>Validation diagnostics</h4> : null}
                      <ul className="runbooks__diagnostic-list">
                        {validationIssues.map((issue, index) => (
                          <li key={`${issue.stage ?? 'stage'}-${issue.step_id ?? 'step'}-${issue.path ?? 'path'}-${index}`}>
                            <div>
                              <strong>{validationIssueTitle(issue)}</strong>
                              <p>{issue.message || 'Compiler validation issue.'}</p>
                              <small>
                                {[issue.step_id ? `Step ${issue.step_id}` : null, issue.path ? `Path ${issue.path}` : null, issue.repairable === false ? 'Not repairable automatically' : null]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </small>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </div>
              ) : null}

              {result.template_ir?.parameters?.length ? (
                <div className="runbooks__section">
                  <h4>Parameters</h4>
                  <ul className="runbooks__parameter-list">
                    {result.template_ir.parameters.map((item) => (
                      <li key={item.name}>
                        <div>
                          <strong>{item.name}</strong>
                          {item.description ? <span>{item.description}</span> : null}
                        </div>
                        <span className={item.required ? 'ui-badge ui-badge--warning' : 'ui-badge ui-badge--ghost'}>{item.required ? 'Required' : 'Optional'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {result.template_ir?.steps?.length ? (
                <div className="runbooks__section">
                  <h4>Steps ({formatNumber(result.template_ir.steps.length)})</h4>
                  <ol className="runbooks__compiled-step-list">
                    {result.template_ir.steps.map((step, index) => (
                      <li key={step.id}>
                        <span>{index + 1}</span>
                        <div>
                          <strong>{step.title || step.id}</strong>
                          <small>{step.description || step.goal || step.capability_ref || step.kind || 'Compiled step'}</small>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}

              {result.assumptions?.length ? (
                <div className="runbooks__section">
                  <h4>Assumptions</h4>
                  <ul className="runbooks__list">
                    {result.assumptions.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {result.runbook_id ? (
                <div className="form-actions">
                  <button type="button" className="ui-button ui-button--primary" onClick={() => navigate(`/app/runbooks/${result.runbook_id}`)}>
                    Open saved runbook
                    <ChevronRightIcon size={16} />
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="runbooks__empty">
              <h3>No compile result</h3>
              <p>This draft has no saved compiler output yet.</p>
            </div>
          )}
        </section>
      </div>
    </OrgModuleLayout>
  );
};

export default RunbookNewPage;
